
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { usePolicyAcknowledgment } from '@/hooks/usePolicyAcknowledgment';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Users, MessageCircle, Eye, UserX, AlertTriangle, FileText, CheckCircle2 } from 'lucide-react';
import { UserBlockingModal } from '@/components/user/UserBlockingModal';
import { ReportAbuseModal } from '@/components/user/ReportAbuseModal';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';

const PrivacySettings = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { user: unifiedUser } = useUnifiedUser();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const user = supabaseUser || internalUser;
  const isInternalUser = !!internalUser && !supabaseUser;

  // Policy acknowledgment status
  const { acknowledgmentData, isLoading: policyLoading } = usePolicyAcknowledgment(
    unifiedUser?.id || null,
    unifiedUser?.userType || null
  );

  // Privacy state
  const [medicalInfoSharing, setMedicalInfoSharing] = useState(false);
  const [connectionRequests, setConnectionRequests] = useState('anyone');
  const [messageVisibility, setMessageVisibility] = useState('anyone');
  const [teamMessages, setTeamMessages] = useState(true);
  const [directMessages, setDirectMessages] = useState('everyone');

  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleSaveChanges = () => {
    // Here you would implement the actual saving logic
    toast.success('Privacy settings saved successfully');
  };

  const handleBlockUser = () => {
    // Modal will handle the blocking interface
  };

  const handleReportAbuse = () => {
    // Modal will handle the reporting interface
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Show appropriate header based on device */}
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className={`${
        isMobile 
          ? 'pt-20 pb-20 px-4' 
          : 'pt-28 pb-6 px-4 md:px-6'
      }`}>
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline" 
              className="border-gray-600 text-gray-400 hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Privacy Settings</h1>
            <p className="text-gray-400">Control how your information is shared and who can contact you</p>
          </div>

          <div className="space-y-6">
            {/* Legal Agreements Status */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Legal Agreements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {policyLoading ? (
                  <div className="text-center py-4 text-gray-400">
                    Loading policy status...
                  </div>
                ) : acknowledgmentData ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4 text-sports-green" />
                          <Label className="text-white font-medium">Privacy Policy</Label>
                        </div>
                        <Badge variant={acknowledgmentData.privacy_policy_accepted ? "default" : "secondary"}>
                          {acknowledgmentData.privacy_policy_accepted ? 'Accepted' : 'Not Accepted'}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-2">
                          Version: {acknowledgmentData.privacy_policy_version}
                        </p>
                      </div>

                      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4 text-sports-green" />
                          <Label className="text-white font-medium">Terms of Service</Label>
                        </div>
                        <Badge variant={acknowledgmentData.tos_accepted ? "default" : "secondary"}>
                          {acknowledgmentData.tos_accepted ? 'Accepted' : 'Not Accepted'}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-2">
                          Version: {acknowledgmentData.tos_version}
                        </p>
                      </div>

                      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4 text-sports-green" />
                          <Label className="text-white font-medium">Cookie Consent</Label>
                        </div>
                        <Badge variant={acknowledgmentData.cookies_accepted ? "default" : "secondary"}>
                          {acknowledgmentData.cookies_accepted ? 'Accepted' : 'Not Accepted'}
                        </Badge>
                      </div>

                      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4 text-sports-green" />
                          <Label className="text-white font-medium">Data Rights</Label>
                        </div>
                        <Badge variant={acknowledgmentData.data_rights_acknowledged ? "default" : "secondary"}>
                          {acknowledgmentData.data_rights_acknowledged ? 'Acknowledged' : 'Not Acknowledged'}
                        </Badge>
                      </div>
                    </div>

                    {acknowledgmentData.parental_consent !== null && (
                      <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-4 w-4 text-primary" />
                          <Label className="text-white font-medium">Parental Consent</Label>
                        </div>
                        <Badge variant={acknowledgmentData.parental_consent ? "default" : "secondary"}>
                          {acknowledgmentData.parental_consent ? 'Granted' : 'Required'}
                        </Badge>
                        {acknowledgmentData.parent_name && (
                          <p className="text-xs text-gray-400 mt-2">
                            Parent/Guardian: {acknowledgmentData.parent_name}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="text-xs text-gray-400 text-center pt-2">
                      Acknowledged on: {new Date(acknowledgmentData.acknowledged_at).toLocaleDateString()} at{' '}
                      {new Date(acknowledgmentData.acknowledged_at).toLocaleTimeString()}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-400">
                    No policy acknowledgment found
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Data Sharing Preferences */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <Shield className="h-6 w-6" />
                  Data Sharing Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-sports-green" />
                    <div>
                      <Label htmlFor="medical-sharing" className="text-white font-medium cursor-pointer">
                        Share Medical Information
                      </Label>
                      <p className="text-gray-400 text-sm">
                        Allow coaches and team owners to view your medical information when you're on their team
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="medical-sharing"
                    checked={medicalInfoSharing}
                    onCheckedChange={setMedicalInfoSharing}
                  />
                </div>

                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="h-5 w-5 text-sports-green" />
                    <Label className="text-white font-medium">Connection Requests</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Choose who can send you connection requests
                  </p>
                  <Select value={connectionRequests} onValueChange={setConnectionRequests}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      <SelectItem value="anyone">Anyone</SelectItem>
                      <SelectItem value="coaches-only">Coaches Only</SelectItem>
                      <SelectItem value="team-owners-only">Team Owners Only</SelectItem>
                      <SelectItem value="coaches-and-owners">Coaches & Team Owners</SelectItem>
                      <SelectItem value="no-one">No One</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Messaging Privacy */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <MessageCircle className="h-6 w-6" />
                  Messaging Privacy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <Eye className="h-5 w-5 text-sports-green" />
                    <Label htmlFor="message-visibility" className="text-white font-medium">Message Visibility</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Control who can send you direct messages
                  </p>
                  <Select value={messageVisibility} onValueChange={setMessageVisibility}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      <SelectItem value="anyone">Anyone</SelectItem>
                      <SelectItem value="friends-only">Friends Only</SelectItem>
                      <SelectItem value="teammates-only">Teammates Only</SelectItem>
                      <SelectItem value="no-one">No One</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-sports-green" />
                    <div>
                      <Label htmlFor="team-messages" className="text-white font-medium cursor-pointer">
                        Team Messages
                      </Label>
                      <p className="text-gray-400 text-sm">
                        Allow teams and coaches to contact you through team chat systems
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="team-messages"
                    checked={teamMessages}
                    onCheckedChange={setTeamMessages}
                  />
                </div>

                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <MessageCircle className="h-5 w-5 text-sports-green" />
                    <Label className="text-white font-medium">Direct Messages</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">
                    Set preferences for who can send you direct messages
                  </p>
                  <Select value={directMessages} onValueChange={setDirectMessages}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      <SelectItem value="everyone">Everyone</SelectItem>
                      <SelectItem value="following-only">People I Follow Only</SelectItem>
                      <SelectItem value="mutual-only">Mutual Connections Only</SelectItem>
                      <SelectItem value="no-one">No One</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* User Blocking and Reporting */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <UserX className="h-6 w-6" />
                  User Blocking and Reporting
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <UserX className="h-5 w-5 text-sports-green" />
                    <Label className="text-white font-medium">Block Users</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    Block users so they can no longer send you messages or see your content
                  </p>
                  <UserBlockingModal>
                    <Button
                      variant="outline"
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      Manage Blocked Users
                    </Button>
                  </UserBlockingModal>
                </div>

                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="h-5 w-5 text-sports-green" />
                    <Label className="text-white font-medium">Report Abuse</Label>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    Report users or content that violates our community guidelines
                  </p>
                  <ReportAbuseModal>
                    <Button
                      variant="outline"
                      className="border-orange-600 text-orange-400 hover:bg-orange-900/20"
                    >
                      Report Abuse
                    </Button>
                  </ReportAbuseModal>
                </div>
              </CardContent>
            </Card>

            {/* Save Changes */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveChanges}
                className="bg-sports-green hover:bg-sports-green/90 text-black"
              >
                Save Privacy Settings
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      <MobileBottomNav />
    </div>
  );
};

export default PrivacySettings;
